import React, { useState } from 'react';
import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import { Field } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { N500A } from '@atlaskit/theme/colors';
import SectionMessage from '@atlaskit/section-message';
import {
  BillingFrequency,
  EntitlementDetails,
  LinkedDestinationEntitlement,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getAppropriatePromotions } from '@atlassian/temp-helper';
import classNames from 'classnames';
import { ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE } from '@atlassian/cssxp-utils-generic';
import { MARKETPLACE_PROMO_CODE, isFreePricingType, PARTNER_DISCOUNT } from '@atlassian/cssxp-utils-entitlement';
import { PromotionItemDetail, SelectPromotionFormFieldV2, SelectPromotionSelectItem } from '../../forms';
import {
  getCustomPromotionReason,
  getPromotionTitle,
  isDiscretionaryDiscountPromotion,
  renderPromotionValue,
} from '../../helper';
import { useFeatureToggle } from '../../hooks';
import { InfoPanel } from '../../info-panel';
import { FieldConfig } from '../../manage-subscription-panel';
import { PartnerPromotionItem } from '../../partner-promotion-item';

import './manage-promotions-section.scss';

export interface CustomValues {
  value: string;
  label: string;
}

export type AddedPromotionsType = {
  customDuration?: CustomValues[];
  customIterations?: string[];
  customReason?: CustomValues[];
  customValue?: string[];
  [key: string]: any;
};

export const filterPromotionItem = (addedPromotions?: AddedPromotionsType) => {
  const selectedPromo: SelectPromotionSelectItem[] = [];
  for (let i = 0; ; i++) {
    const promotionItem = get(addedPromotions, `promotionId-${i}`, '');
    if (promotionItem) {
      if (isDiscretionaryDiscountPromotion(promotionItem)) {
        const customPromotion = get(addedPromotions, `customPromotionId-${i}`, '');
        // adding customFormField to that we can easily re-populate the fields on coming back to
        // manage promotion step
        customPromotion.promotion.customFormField = {
          iterations: defaultTo(get(addedPromotions, 'customIterations'), [])[i],
          value: defaultTo(get(addedPromotions, 'customValue'), [])[i],
          reason: defaultTo(get(addedPromotions, 'customReason'), [])[i],
          duration: defaultTo(get(addedPromotions, 'customDuration'), [])[i],
        };
        selectedPromo.push(customPromotion);
      } else {
        selectedPromo.push(promotionItem);
      }
    } else break;
  }
  return selectedPromo;
};

type GetAppropriateAvailablePromotionsType = {
  availablePromotions?: (Promotion | null)[] | null;
  appropriateScheduledPromotions: Promotion[];
  activePromotions: Promotion[];
  billingFrequency?: string;
  isEntitlementRelationshipEnabled: boolean;
  isApp: boolean;
};

const getAppropriateAvailablePromotions = ({
  availablePromotions,
  appropriateScheduledPromotions,
  activePromotions,
  billingFrequency,
  isEntitlementRelationshipEnabled,
  isApp,
}: GetAppropriateAvailablePromotionsType) => {
  const appropriateAvailablePromotions = getAppropriatePromotions(availablePromotions, billingFrequency);
  const isMarketPlacePromocodeCurrentPromotionApplied = (activePromotions || []).some(
    (item: any) => item.type === 'PROMO_CODE'
  );
  const isMarketPlacePromocodeScheduledPromotionApplied = (appropriateScheduledPromotions || []).some(
    (item: any) => item.type === 'PROMO_CODE'
  );

  if (
    isEntitlementRelationshipEnabled &&
    isApp &&
    !isMarketPlacePromocodeScheduledPromotionApplied &&
    !isMarketPlacePromocodeCurrentPromotionApplied
  ) {
    appropriateAvailablePromotions.push({
      type: MARKETPLACE_PROMO_CODE,
      title: 'Promo code',
      status: 'ACTIVE',
    });
  }

  return appropriateAvailablePromotions;
};

interface PromotionListProps {
  appliedPromotions?: Promotion[];
  promotionsToRemove: SelectPromotionSelectItem[];
  schedulePromotionsToRemove: SelectPromotionSelectItem[];
  setPromotionsToRemove: (promotions: SelectPromotionSelectItem[]) => void;
  availablePromotionTypes: string[];
  setAvailablePromotionTypes: (promotionType: string[]) => void;
  isRemoveNotAllowed?: boolean;
  isScheduled?: boolean;
}

const PromotionList = ({
  appliedPromotions = [],
  promotionsToRemove,
  schedulePromotionsToRemove,
  setPromotionsToRemove,
  availablePromotionTypes,
  setAvailablePromotionTypes,
  isRemoveNotAllowed,
  isScheduled,
}: PromotionListProps) => {
  const removedPromotions = isScheduled ? schedulePromotionsToRemove : promotionsToRemove;

  const onRemovePromotion = (id: string, promotionType: string) => {
    const isUnremovingPromotion = removedPromotions.some(({ promotion }) => promotion?.id === id);
    let updatedPromotions = removedPromotions;

    if (isUnremovingPromotion) {
      updatedPromotions = removedPromotions.filter(({ promotion }) => promotion?.id !== id);
      setAvailablePromotionTypes([...availablePromotionTypes, promotionType]);
    } else {
      const promotionToBeRemoved = appliedPromotions.find(({ id: promotionId }) => id === promotionId);
      if (promotionToBeRemoved) {
        updatedPromotions = [
          ...removedPromotions,
          { promotion: promotionToBeRemoved, value: promotionToBeRemoved?.id || '' },
        ];
        const promotionsTypeToRemove = availablePromotionTypes.filter((type) => promotionType !== type);
        setAvailablePromotionTypes(promotionsTypeToRemove);
      }
    }

    setPromotionsToRemove(updatedPromotions);
  };

  return (
    <>
      {defaultTo(appliedPromotions, []).map((item, index) => {
        let isRemoved = removedPromotions.some(
          ({ promotion }: SelectPromotionSelectItem) => promotion?.id === item?.id
        );
        if (isScheduled && promotionsToRemove?.length) {
          isRemoved = true;
        }
        const isPartnerDiscount = item?.type === PARTNER_DISCOUNT;
        return (
          <div
            data-testid={isRemoved ? 'removed-title' : 'added-title'}
            key={index}
            className="promotion-list-item"
            style={{ marginBottom: '1%' }}
          >
            <strong className={classNames({ 'remove-on': isRemoved }, 'promotion-list-item-header')}>
              {getPromotionTitle(item, { handleEmptyTitle: true })}
            </strong>
            {isPartnerDiscount && item?.applicationReason?.readableName && (
              <Tooltip content={getCustomPromotionReason(item)} tag={'span'}>
                <InfoIcon size="small" testId="info-icon" label="Info" primaryColor={N500A} />
              </Tooltip>
            )}
            <div className="remove-promotion-link-container">
              <p className={classNames({ 'remove-on': isRemoved })}>
                {renderPromotionValue(item, { showDuration: true, showReason: true })}
              </p>
              {(!isScheduled || (isScheduled && !promotionsToRemove?.length)) && (
                <Button
                  isDisabled={isRemoveNotAllowed || isPartnerDiscount} // cannot remove partner discount
                  className={classNames({ remove: !isRemoved })}
                  appearance="link"
                  testId="remove-button"
                  aria-label="Remove promotion"
                  onClick={() => onRemovePromotion(defaultTo(item?.id, ''), defaultTo(item?.title, ''))}
                >
                  {isRemoved ? 'Undo removal' : 'Remove'}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

export interface PromotionFieldConfig {
  promotions?: boolean; // complete promotions panel
  activePromotions?: boolean; // only active promotions
  schedulePromotions?: boolean; // only scheduled promotions
  addPromotions?: boolean;
}
interface ManagePromotionsSectionProps {
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  isHidden?: FieldConfig;
  isDisabled?: PromotionFieldConfig;
  isLoading?: PromotionFieldConfig;
  fieldName?: string;
  offeringPricingType?: string | null;
  billingFrequency?: BillingFrequency;
  availablePromotions?: (Promotion | null)[] | null;
  partnerPromotion?: Promotion | null;
  selectedPromotionsToAdd?: AddedPromotionsType;
  selectedPromotionsToRemove?: SelectPromotionSelectItem[];
  selectedSchedulePromotionsToRemove?: SelectPromotionSelectItem[];
  onChange?: (fieldName: string, value: SelectPromotionSelectItem[] | PromotionItemDetail) => void;
}

export const ManagePromotionsSection = ({
  availablePromotions,
  isDisabled,
  isHidden,
  fieldName,
  offeringPricingType,
  entitlement,
  billingFrequency,
  partnerPromotion,
  selectedPromotionsToAdd,
  selectedPromotionsToRemove = [],
  selectedSchedulePromotionsToRemove = [],
  onChange,
  isLoading,
}: ManagePromotionsSectionProps) => {
  const [isEnabled] = useFeatureToggle();
  const fieldNamePrefix = fieldName ? `${fieldName}.` : '';

  const [promotionsToRemove, setPromotionsToRemove] = useState<SelectPromotionSelectItem[]>(selectedPromotionsToRemove);
  const [schedulePromotionsToRemove, setSchedulePromotionsToRemove] = useState<SelectPromotionSelectItem[]>(
    selectedSchedulePromotionsToRemove
  );

  const activePromotions = defaultTo(entitlement?.promotions, []).filter((promotion) => !!promotion) as Promotion[];

  const schedulePromotions = entitlement?.scheduledChanges?.promotions?.map((promotion, index) => ({
    ...promotion,
    index: index.toString(),
  }));

  const filteredSelectedPromotionsToAdd = defaultTo(filterPromotionItem(selectedPromotionsToAdd), [])
    .filter((selectedPromo: SelectPromotionSelectItem) => !!selectedPromo?.promotion?.id)
    .map((item) => item?.promotion) as Promotion[];

  const appropriateScheduledPromotions = getAppropriatePromotions(schedulePromotions, billingFrequency);

  const appropriateAvailablePromotions = getAppropriateAvailablePromotions({
    availablePromotions,
    activePromotions,
    appropriateScheduledPromotions,
    isApp: !!entitlement?.isApp,
    billingFrequency,
    isEntitlementRelationshipEnabled: isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE),
  });

  const allPromotionTypes =
    [...(activePromotions ?? []), ...(schedulePromotions ?? [])].map((item: any) => item.type) ?? [];
  if (partnerPromotion) {
    allPromotionTypes.push(partnerPromotion.type);
  }
  const [availablePromotionTypes, setAvailablePromotionTypes] = useState<string[]>(allPromotionTypes);

  const hasActivePartnerPromotion = (activePromotions ?? []).some((promotion) => promotion?.type === PARTNER_DISCOUNT);
  const hasScheduledPartnerPromotion = (schedulePromotions ?? []).some(
    (promotion) => promotion?.type === PARTNER_DISCOUNT
  );

  const customFieldNames = [
    `${fieldNamePrefix}customValue`,
    `${fieldNamePrefix}customDuration`,
    `${fieldNamePrefix}customIterations`,
    `${fieldNamePrefix}customReason`,
    `${fieldNamePrefix}customPromotionId`,
  ];

  return (
    <div className="manage-promotions-section">
      {!!activePromotions?.length && !isHidden?.activePromotions && (
        <Field
          defaultValue={promotionsToRemove}
          name={`${fieldNamePrefix}promotionsToRemove`}
          id={`${fieldNamePrefix}promotionsToRemove`}
          label="Current discounts"
        >
          {({ fieldProps }) => (
            <div data-testid="currentPromotionBox">
              <PromotionList
                {...fieldProps}
                appliedPromotions={activePromotions}
                promotionsToRemove={promotionsToRemove}
                schedulePromotionsToRemove={schedulePromotionsToRemove}
                setPromotionsToRemove={(promotions) => {
                  onChange && onChange(fieldProps.name, promotions);
                  setPromotionsToRemove(promotions);
                }}
                setAvailablePromotionTypes={setAvailablePromotionTypes}
                availablePromotionTypes={availablePromotionTypes}
                isRemoveNotAllowed={!!isDisabled?.activePromotions || !!isDisabled?.promotions}
              />
            </div>
          )}
        </Field>
      )}
      {!!schedulePromotions?.length && !isHidden?.schedulePromotions ? (
        <Field
          defaultValue={schedulePromotionsToRemove}
          name={`${fieldNamePrefix}schedulePromotionsToRemove`}
          id={`${fieldNamePrefix}schedulePromotionsToRemove`}
          label="Discounts to be applied to next bill"
        >
          {({ fieldProps }) => (
            <>
              {promotionsToRemove?.length > 0 && (
                <div style={{ marginBottom: '1%' }}>
                  <InfoPanel
                    type="warning"
                    message="Removal of any active promotion will also remove all scheduled promotions."
                  />
                </div>
              )}
              <div data-testid="schedulePromotionBox">
                {partnerPromotion &&
                  // do not auto-apply partner promotion if entitlement already has active partner promotion
                  !(hasActivePartnerPromotion && !isHidden?.activePromotions) &&
                  // do not auto-apply partner promotion if entitlement already has scheduled partner promotion
                  !hasScheduledPartnerPromotion && (
                    <PartnerPromotionItem partnerPromotion={partnerPromotion} fieldNamePrefix={fieldNamePrefix} />
                  )}
                <PromotionList
                  {...fieldProps}
                  appliedPromotions={schedulePromotions}
                  promotionsToRemove={promotionsToRemove}
                  schedulePromotionsToRemove={schedulePromotionsToRemove}
                  setPromotionsToRemove={(promotions) => {
                    onChange && onChange(fieldProps.name, promotions);
                    setSchedulePromotionsToRemove(promotions);
                  }}
                  setAvailablePromotionTypes={setAvailablePromotionTypes}
                  availablePromotionTypes={availablePromotionTypes}
                  isRemoveNotAllowed={!!isDisabled?.schedulePromotions || !!isDisabled?.promotions}
                  isScheduled
                />
              </div>
            </>
          )}
        </Field>
      ) : (
        <>
          {partnerPromotion &&
            // do not auto-apply partner promotion if entitlement already has active partner promotion
            !(hasActivePartnerPromotion && !isHidden?.activePromotions) &&
            !isFreePricingType(offeringPricingType) && (
              <div>
                <h3>
                  <span className="partner-promotion-header">Discounts to be applied to next bill</span>
                </h3>
                <div data-testid="partnerPromotionBox">
                  <PartnerPromotionItem partnerPromotion={partnerPromotion} fieldNamePrefix={fieldNamePrefix} />
                </div>
              </div>
            )}
        </>
      )}
      {isFreePricingType(offeringPricingType) ? (
        <SectionMessage appearance="warning">
          <strong>No discounts can be added to a Free plan</strong>
        </SectionMessage>
      ) : (
        isHidden?.addPromotions ||
        (isLoading?.addPromotions ? (
          <Field<ValueType<string, false>> label="Add discount" name="discountLoading" isDisabled defaultValue={''}>
            {({ fieldProps }) => (
              <Select {...fieldProps} isLoading isDisabled className="add-discount-loading" placeholder="Select..." />
            )}
          </Field>
        ) : (
          <SelectPromotionFormFieldV2
            availablePromotions={appropriateAvailablePromotions}
            promotions={filteredSelectedPromotionsToAdd}
            singleFieldLabel={'Add discount'}
            fieldNamePrefix={fieldNamePrefix}
            availablePromotionTypes={availablePromotionTypes}
            productId={entitlement?.productId}
            isDisabled={!!isDisabled?.addPromotions || !!isDisabled?.promotions}
            customFieldNames={customFieldNames}
            onAfterRemovePromotion={(promotionValues) => {
              onChange && onChange('removePromotion', promotionValues);
            }}
            onCustomValueChange={(customPromotion) => {
              onChange && onChange('customPromotion', customPromotion);
            }}
            onAfterAddPromotion={(promotionItem) => {
              onChange && onChange('addPromotion', promotionItem);
            }}
          />
        ))
      )}
    </div>
  );
};
